import { parse } from 'date-fns';

import {
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	SegmentFragment
} from '@websky/graphql';
import { PassengerState } from '../Checkout/store/passengers/reducers';
import { SelectedServicesState } from '../Checkout/store/selectedServices/reducers';
import { AvailableSegment, AvailableSegmentPassenger, ServicePassengerSelected, ServicesMap } from './types';
import { Reducer } from './hooks';
import { API_DATE_FORMAT, format, getTravellerFullName } from '../utils';

export const getAvailableSegments = (
	segments: SegmentFragment[],
	passengers: PassengerState[],
	servicesMap: ServicesMap
): AvailableSegment[] => {
	const servicesSegmentIds = new Set(Object.keys(servicesMap));
	const availableSegments: AvailableSegment[] = [];

	for (const segment of segments) {
		if (!servicesSegmentIds.has(segment.id)) {
			continue;
		}

		const passengerIds = new Set(Object.keys(servicesMap[segment.id]));
		const passengersArray: AvailableSegmentPassenger[] = [];

		for (const passenger of passengers) {
			if (passengerIds.has(passenger.id)) {
				passengersArray.push({
					passengerId: passenger.id,
					segmentId: segment.id,
					serviceId: servicesMap[segment.id][passenger.id],
					name: getTravellerFullName(passenger)
				});
			}
		}

		availableSegments.push({
			segmentId: segment.id,
			passengers: passengersArray,
			direction: `${segment.departure.airport.city.name} – ${segment.arrival.airport.city.name}`,
			departure: segment.departure.airport.city.name,
			arrival: segment.arrival.airport.city.name,
			date: format(parse(segment.departure.date, API_DATE_FORMAT, new Date()), 'dd MMM')
		});
	}

	return availableSegments;
};

type OrderServiceFragment =
	| GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
	| GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;

const isServiceAllowedFor = (service: OrderServiceFragment, segmentId: string, passengerId: string) => {
	return (
		service.allowedSegments.some(ids => ids.includes(segmentId)) &&
		service.allowedPassengers.some(ids => ids.includes(passengerId))
	);
};

const isSameServiceGroup = (service: OrderServiceFragment, reference: OrderServiceFragment) => {
	if (service.rfisc !== reference.rfisc) {
		return false;
	}

	const serviceSubType = 'subType' in service ? service.subType : undefined;
	const referenceSubType = 'subType' in reference ? reference.subType : undefined;

	if (serviceSubType === undefined || referenceSubType === undefined) {
		return true;
	}

	return serviceSubType === referenceSubType;
};

export const getMinCanBeAddedServiceId = (
	services: OrderServiceFragment[],
	segmentId: string,
	passengerId: string,
	referenceService?: OrderServiceFragment
): string | undefined => {
	const canBeAddedServices = services.filter(
		service => !!service.canBeAdded && isServiceAllowedFor(service, segmentId, passengerId)
	);

	const groupServices = referenceService
		? canBeAddedServices.filter(service => isSameServiceGroup(service, referenceService))
		: [];
	const candidates = groupServices.length > 0 ? groupServices : canBeAddedServices;

	let minService: OrderServiceFragment | undefined;

	for (const service of candidates) {
		if (!minService || Number(service.id) < Number(minService.id)) {
			minService = service;
		}
	}

	return minService?.id;
};

interface ServiceCanModifyParams {
	service: OrderServiceFragment | undefined;
	services: OrderServiceFragment[];
	segmentId: string;
	passengerId: string;
	count: number;
	confirmedCount?: number;
	isCheckboxOrSwitch: boolean;
}

export const getIsServiceCanModify = ({
	service,
	services,
	segmentId,
	passengerId,
	count,
	confirmedCount,
	isCheckboxOrSwitch
}: ServiceCanModifyParams): boolean => {
	if (service?.canBeAdded || (confirmedCount !== undefined && count > confirmedCount)) {
		return true;
	}

	if (!isCheckboxOrSwitch || count !== 0) {
		return false;
	}

	const isConfirmed = confirmedCount !== undefined && confirmedCount > 0 && count === confirmedCount;

	return !isConfirmed && !!getMinCanBeAddedServiceId(services, segmentId, passengerId, service);
};

export const computeServicesMap = (
	services: OrderServiceFragment[],
	selectedServices: SelectedServicesState,
	canAddedOne?: boolean
) => {
	const servicesMap = new Map(services.map(service => [service.id, service]));

	const computedServices: ServicesMap = {};

	for (const passengerId in selectedServices) {
		for (const serviceId in selectedServices[passengerId]) {
			if (!servicesMap.has(serviceId)) {
				continue;
			}

			selectedServices[passengerId][serviceId].forEach(selectedService => {
				selectedService.segments.forEach(segmentId => {
					if (!computedServices[segmentId]) {
						computedServices[segmentId] = {};
					}

					computedServices[segmentId][passengerId] = serviceId;
				});
			});
		}
	}

	for (const service of services) {
		const segmentIds = service.allowedSegments.reduce((arr, ids) => [...arr, ...ids], []);
		const passengerIds = service.allowedPassengers.reduce((arr, ids) => [...arr, ...ids], []);

		for (const segmentId of segmentIds) {
			if (!computedServices[segmentId]) {
				computedServices[segmentId] = {};
			}

			for (const passengerId of passengerIds) {
				const existServiceId = computedServices[segmentId][passengerId];

				if (!existServiceId) {
					computedServices[segmentId][passengerId] =
						(canAddedOne && getMinCanBeAddedServiceId(services, segmentId, passengerId, service)) ||
						service.id;
				} else if (existServiceId !== service.id && !canAddedOne) {
					computedServices[segmentId][passengerId] = service.id;
				}
			}
		}
	}

	return computedServices;
};

export const getNewSelectedServices = (
	serviceMap: ServicesMap,
	selectedServicesState: Reducer,
	allSegments?: boolean
) => {
	const selectedServices: ServicePassengerSelected[] = [];

	const selectedSegments = Object.keys(selectedServicesState);
	const availableSegments = Object.keys(serviceMap);

	if (allSegments) {
		const firstSegment = selectedSegments[0];
		const selectedPassengers = selectedServicesState?.[firstSegment]
			? Object.keys(selectedServicesState[firstSegment])
			: [];

		selectedPassengers.forEach(passenger => {
			availableSegments.forEach(segmentId => {
				selectedServices.push({
					count: selectedServicesState[firstSegment][passenger],
					serviceId: serviceMap[segmentId]?.[passenger] ?? serviceMap[firstSegment][passenger],
					segmentIds: [segmentId],
					passengerId: passenger
				});
			});
		});
	} else {
		availableSegments.forEach(segment => {
			const passengers = Object.keys(serviceMap[segment]);

			passengers.forEach(passenger => {
				if (selectedServicesState[segment]?.hasOwnProperty(passenger)) {
					selectedServices.push({
						serviceId: serviceMap[segment][passenger],
						segmentIds: [segment],
						count: selectedServicesState[segment][passenger],
						passengerId: passenger
					});
				}
			});
		});
	}

	return selectedServices;
};

export const getIsDifferentServicesOrPassengers = (servicesMap: ServicesMap) => {
	const serviceIdsBySegmentMap = new Map<string, Set<string>>();
	const passengerIdsBySegmentMap = new Map<string, Set<string>>();

	if (Object.keys(servicesMap ?? {})?.length > 1) {
		for (const segmentId in servicesMap) {
			for (const passengerId in servicesMap[segmentId]) {
				const serviceId = servicesMap[segmentId][passengerId];

				const serviceIds = [...(serviceIdsBySegmentMap.get(segmentId) ?? [])];
				const passengerIds = [...(passengerIdsBySegmentMap.get(segmentId) ?? [])];

				serviceIdsBySegmentMap.set(segmentId, new Set([...serviceIds, serviceId]));
				passengerIdsBySegmentMap.set(segmentId, new Set([...passengerIds, passengerId]));
			}
		}

		const serviceIdsBySegment = [...serviceIdsBySegmentMap.values()];
		const isDifferentServices = serviceIdsBySegment.some(
			serviceIds => JSON.stringify([...serviceIds]) !== JSON.stringify([...serviceIdsBySegment[0]])
		);

		if (isDifferentServices) {
			return true;
		}

		const passengerIdsBySegment = [...passengerIdsBySegmentMap.values()];
		const isDifferentPassengers = passengerIdsBySegment.some(
			passengerIds => JSON.stringify([...passengerIds]) !== JSON.stringify([...passengerIdsBySegment[0]])
		);

		if (isDifferentPassengers) {
			return true;
		}
	}

	return false;
};

export const getVipServiceStateSelectedKeys = (state: Reducer) => {
	const keys = new Set<string>();

	for (const segmentId in state) {
		for (const passengerId in state[segmentId]) {
			const serviceCount = state[segmentId][passengerId];
			if (serviceCount) {
				keys.add(`${segmentId}-${passengerId}-${serviceCount}`);
			}
		}
	}

	return [...keys].sort();
};
